import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { ScreenLoader } from "@/components/loaders";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { useLazyGetUserPermissionsQuery } from "@/services/AuthService";
import { initializeRolePermissions } from "@/services/RolePermissionService";

const RequireAuth = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const location = useLocation();

  const token = useSelector((state: RootState) => state?.auth?.token);
  const loggedOutDueToError = useSelector(
    (state: RootState) => state?.auth?.loggedOutDueToError,
  );
  const userPermissions = useSelector(
    (state: RootState) => state?.auth?.userPermissions,
  );
  const [getUserPermissions] = useLazyGetUserPermissionsQuery();

  useEffect(() => {
    const refreshPermissions = async () => {
      try {
        const permissionsResponse = await getUserPermissions({
          lang: "en",
        }).unwrap();

        if (permissionsResponse && permissionsResponse.length > 0) {
          initializeRolePermissions(permissionsResponse);
        }
      } catch (error) {
        console.error("Failed to fetch user permissions:", error);
      }
    };

    if (!token) {
      setIsLoading(false);
      return;
    }

    if (!userPermissions) {
      // First load in this browser session: block until we know the user's permissions.
      refreshPermissions().finally(() => setIsLoading(false));
      return;
    }

    // Cached permissions already let us render immediately. `dynamicRolePermissions` is a
    // plain module variable (not persisted), so a hard reload resets it to the hardcoded
    // defaults - refresh it from the server in the background so route-guard checks stay
    // accurate without blocking the route tree on every navigation.
    setIsLoading(false);
    refreshPermissions();
  }, [token, userPermissions, getUserPermissions]);

  if (isLoading) {
    return <ScreenLoader />;
  }

  return token ? (
    <Outlet />
  ) : (
    <Navigate
      to="/auth/login"
      state={loggedOutDueToError ? undefined : { from: location }}
      replace
    />
  );
};

export { RequireAuth };
